import { generateKeywordsQueryForOr } from '@smarttransit/common-client'
import { findTransportationProfiles } from '../../services/transportation-profiles-service'
import { findTransportationOwnerProfiles } from '../../services/transportation-owners-service'
import { addAlert } from '../../utilities/helpers'

export default {
  name: 'transportation-profile-autocomplete',
  props: {
    value: {
      type: [Object, Array],
      default: null
    },
    label: {
      type: String,
      default: 'Search vehicle'
    },
    placeholder: {
      type: String,
      default: 'Filter by licence plate, driver name'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    transportationOwnerId: {
      type: Number,
      default: null
    },
    clearable: {
      type: Boolean,
      default: true
    },
    showDriver: {
      type: Boolean,
      default: true
    },
    showOwnerAccount: {
      type: Boolean,
      default: false
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    showRecentWhenEmpty: {
      type: Boolean,
      default: false
    },
    recentProfilesLimit: {
      type: Number,
      default: 20
    },
    searchDelay: {
      type: Number,
      default: 800
    }
  },
  data () {
    return {
      isLoading: false,
      isFocused: false,
      searchKey: '',
      internalValue: this.multiSelect ? [] : null,
      transportationProfiles: [],
      searchHandle: null
    }
  },
  computed: {
    selectedProfilesForDisplay () {
      return this.getSelectedProfiles()
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler (newValue) {
        if (this.multiSelect) {
          const selectedProfiles = Array.isArray(newValue) ? newValue.filter(Boolean) : []
          if (!this.profileListsMatch(this.internalValue, selectedProfiles)) {
            this.suppressNextEmit = true
            this.internalValue = selectedProfiles
          }
          return
        }

        const resolvedValue = newValue || null
        if (resolvedValue !== this.internalValue) {
          this.suppressNextEmit = true
          this.internalValue = resolvedValue
        }

        if (newValue) {
          this.addSelectedProfilesToOptions([newValue])
        }
      }
    },
    internalValue (newValue) {
      // Skip the echo emission when the change came from the value prop,
      // so parent @input handlers only fire for user-driven changes.
      if (this.suppressNextEmit) {
        this.suppressNextEmit = false
        return
      }

      if (this.multiSelect) {
        this.$emit('input', Array.isArray(newValue) ? newValue.slice() : [])
        return
      }

      this.$emit('input', newValue || null)
    },
    transportationOwnerId () {
      this.runSearch(this.searchKey)
    },
    searchKey (newValue) {
      this.runSearch(newValue)
    }
  },
  mounted () {
    this.runSearch(this.searchKey)
  },
  beforeDestroy () {
    clearTimeout(this.searchHandle)
  },
  methods: {
    onFocus () {
      this.isFocused = true
    },
    onBlur () {
      this.isFocused = false

      // Reset the search text so the next open lists selected/recent profiles
      // and the truncated selection summary becomes visible again.
      if (this.multiSelect && this.searchKey) {
        this.searchKey = ''
      }
    },
    formatSelectionLabel (profile) {
      if (!profile) {
        return ''
      }

      return profile.licensePlateNumber || profile.shortcodeId || profile.id || ''
    },
    getSelectedProfiles () {
      if (this.multiSelect) {
        return Array.isArray(this.internalValue) ? this.internalValue.filter(Boolean) : []
      }

      return this.internalValue ? [this.internalValue] : []
    },
    isProfileSelected (profileId) {
      if (!profileId) {
        return false
      }

      return this.getSelectedProfiles().some((profile) => profile && profile.id === profileId)
    },
    profileListsMatch (leftProfiles, rightProfiles) {
      const leftIds = (Array.isArray(leftProfiles) ? leftProfiles : []).map((item) => item && item.id).filter(Boolean)
      const rightIds = (Array.isArray(rightProfiles) ? rightProfiles : []).map((item) => item && item.id).filter(Boolean)

      if (leftIds.length !== rightIds.length) {
        return false
      }

      for (let i = 0; i < leftIds.length; i++) {
        if (leftIds[i] !== rightIds[i]) {
          return false
        }
      }

      return true
    },
    addSelectedProfilesToOptions (profiles) {
      const selectedProfiles = Array.isArray(profiles) ? profiles.filter((item) => item && item.id) : []
      if (!selectedProfiles.length) {
        return
      }

      const options = this.transportationProfiles.slice()
      selectedProfiles.forEach((profile) => {
        const exists = options.find((item) => item.id === profile.id)
        if (!exists) {
          options.unshift(profile)
        }
      })

      this.transportationProfiles = options
    },
    buildFilter (keywords) {
      const include = []
      if (this.showDriver) {
        include.push({ currentDriverObj: 'stUser' })
      }

      const filter = {
        include,
        limit: !keywords && this.showRecentWhenEmpty ? (this.recentProfilesLimit || 20) : 20
      }

      if (!keywords && this.showRecentWhenEmpty) {
        filter.order = 'dateCreated DESC'
      }

      if (this.transportationOwnerId) {
        filter.join = {
          relation: 'transportationOwnerProfile',
          scope: { where: { transportationOwnerId: this.transportationOwnerId } }
        }
      }

      if (keywords) {
        const keywordFilter = generateKeywordsQueryForOr(keywords, ['licensePlateNumber', 'shortcodeId'])
        if (keywordFilter && keywordFilter.length) {
          filter.where = {
            or: keywordFilter
          }
        }
      }

      return filter
    },
    async enrichOwnerAccountNames (profiles) {
      if (!this.showOwnerAccount || !profiles.length) {
        return profiles
      }

      try {
        const profileIds = profiles.map((item) => item.id).filter(Boolean)
        if (!profileIds.length) {
          return profiles
        }

        const ownerProfiles = await findTransportationOwnerProfiles({
          where: { transportationProfileId: { inq: profileIds } },
          include: 'transportationOwner'
        })

        const ownerByProfileId = {}
        ;(ownerProfiles || []).forEach((item) => {
          ownerByProfileId[item.transportationProfileId] = item
        })

        return profiles.map((profile) => {
          const resolved = Object.assign({}, profile)
          const ownerProfile = ownerByProfileId[profile.id]
          resolved.ownerAccountName = ownerProfile && ownerProfile.transportationOwner
            ? ownerProfile.transportationOwner.companyName
            : ''
          return resolved
        })
      } catch (err) {
        return profiles
      }
    },
    runSearch (keywords) {
      clearTimeout(this.searchHandle)
      this.searchHandle = setTimeout(async () => {
        await this.searchTransportationProfiles(keywords)
      }, this.searchDelay || 800)
    },
    async searchTransportationProfiles (keywords) {
      this.isLoading = true
      try {
        const trimmedKeywords = String(keywords || '').trim()
        if (!trimmedKeywords && !this.transportationOwnerId && !this.showRecentWhenEmpty) {
          this.transportationProfiles = this.getSelectedProfiles()
          return
        }

        const filter = this.buildFilter(trimmedKeywords)
        const profiles = await findTransportationProfiles(filter)
        const mappedProfiles = (profiles || []).map((item) => {
          const resolved = Object.assign({}, item)
          resolved.currentDriverName = resolved.currentDriverObj && resolved.currentDriverObj.stUser
            ? ((resolved.currentDriverObj.stUser.firstName || '') + ' ' + (resolved.currentDriverObj.stUser.lastName || '')).trim()
            : ''
          return resolved
        })

        const withOwnerNames = await this.enrichOwnerAccountNames(mappedProfiles)

        if (!trimmedKeywords) {
          // With no keywords, list the selected profiles first so the user
          // can review and uncheck them without searching.
          const selectedProfiles = this.getSelectedProfiles()
          const selectedIdMap = {}
          selectedProfiles.forEach((profile) => {
            selectedIdMap[profile.id] = true
          })

          this.transportationProfiles = selectedProfiles.concat(
            withOwnerNames.filter((profile) => !selectedIdMap[profile.id])
          )
        } else {
          this.transportationProfiles = withOwnerNames

          if (!this.multiSelect) {
            this.addSelectedProfilesToOptions(this.getSelectedProfiles())
          }
        }
      } catch (err) {
        addAlert({
          message: `Error in vehicle search: ${err && err.error ? err.error.message : (err && err.message ? err.message : JSON.stringify(err))}`,
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    }
  }
}
